import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { invoicesService } from "@/api/invoices";
import { companyService } from "@/api/companies";
// import { useSelector } from "react-redux";
import CompanySelection from "@pages/admin/invoices/CompanySelection";
import { useTranslation } from "@/hooks/useTranslation";

const EditInvoice = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // const { user } = useSelector((state) => state.auth);
    // const userId = user?.id;

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [items, setItems] = useState([]);
    const [tax, setTax] = useState(19);
    const [year, setYear] = useState(2026);
    const [formData, setFormData] = useState({
        companyId: "",
        creationDate: new Date().toISOString().split("T")[0],
        dueDate: "",
        notes: "",
    });

    // Fetch invoice data
    const { data: invoice, isLoading: invoiceLoading, error } = useQuery({
        queryKey: ["invoice", id],
        queryFn: () => invoicesService.getInvoice(id),
        retry: false,
    });

    // Fetch companies
    const { data: companiesResponse, isLoading: companiesLoading } = useQuery({
        queryKey: ["companies"],
        queryFn: () => companyService.getCompanies(1, 100),
    });

    const updateMutation = useMutation({
        mutationFn: (data) => invoicesService.updateInvoice(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["invoice", id]);
            queryClient.invalidateQueries(["invoices"]);
            alert(t("invoices.admin.updateSuccess"));
            navigate(`/admin/factures/${id}`);
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert(t("invoices.admin.updateError"));
        },
    });

    // Get companies from response (adjust based on your API structure)
    const companies = companiesResponse?.data || companiesResponse || [];

    // Initialize form data when invoice and companies are loaded
    useEffect(() => {

        if (invoice && companies.length > 0) {
            // Format dates for input fields
            setYear(new Date(invoice.creationDate).getFullYear())
            const formatDateForInput = (dateString) => {
                if (!dateString) return "";
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
            };

            // Get current company from invoice
            const currentCompanyId = invoice.company?.id || invoice.companyId;

            // Find the company in the companies list
            const company = companies.find(c => c.id == currentCompanyId);

            // Set selected company
            setSelectedCompany(company);

            // Calculate tax rate from invoice data
            let taxRate = 19; // default
            if (invoice.taxRate) {
                taxRate = invoice.taxRate;
            } else if (invoice.tax && invoice.subtotal && invoice.discountAmount) {
                // Calculate tax rate from tax amount
                const taxableAmount = invoice.subtotal - invoice.discountAmount;
                taxRate = taxableAmount > 0 ? (invoice.tax / taxableAmount) * 100 : 19;
            }
            setTax(taxRate);

            // Set form data
            setFormData({
                companyId: currentCompanyId || "",
                creationDate: formatDateForInput(invoice.creationDate),
                dueDate: formatDateForInput(invoice.dueDate),
                notes: invoice.notes || "",
            });

            // Set items
            if (invoice.items && invoice.items.length > 0) {
                const formattedItems = invoice.items.map(item => {
                    const quantity = parseFloat(item.quantity) || 1;
                    const unitPrice = parseFloat(item.unitPrice) || 0;
                    const discountPercentage = parseFloat(item.discountPercentage) || 0;
                    const subtotal = quantity * unitPrice;
                    const discountValue = subtotal * (discountPercentage / 100);

                    return {
                        id: item.id || null,
                        name: item.name || "",
                        quantity: quantity,
                        unitPrice: unitPrice,
                        discountPercentage: discountPercentage,
                        discountValue: discountValue,
                        subtotal: subtotal,
                    };
                });
                setItems(formattedItems);
            } else {
                setItems([{
                    id: null,
                    name: "",
                    quantity: 1,
                    unitPrice: 0,
                    discountPercentage: 0,
                    discountValue: 0,
                    subtotal: 0,
                }]);
            }
        }
    }, [invoice, companies]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // When companyId changes, update selectedCompany
        if (name === "companyId" && companies.length > 0) {
            const company = companies.find(c => c.id == value);
            setSelectedCompany(company);
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];

        if (field === "name" || field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
            newItems[index] = { ...newItems[index], [field]: value };
        }

        // Recalculate item discount value and subtotal
        if (field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
            const quantity = parseFloat(field === "quantity" ? value : newItems[index].quantity) || 0;
            const unitPrice = parseFloat(field === "unitPrice" ? value : newItems[index].unitPrice) || 0;
            const discountPercentage = parseFloat(field === "discountPercentage" ? value : newItems[index].discountPercentage) || 0;

            const subtotal = quantity * unitPrice;
            const discountValue = subtotal * (discountPercentage / 100);

            newItems[index].subtotal = subtotal;
            newItems[index].discountValue = discountValue;
        }

        setItems(newItems);
    };

    const addItem = () => {
        setItems([
            ...items,
            {
                id: null,
                name: "",
                quantity: 1,
                unitPrice: 0,
                discountPercentage: 0,
                discountValue: 0,
                subtotal: 0,
            }
        ]);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const calculateTotals = () => {
        let subtotal = 0;
        let totalDiscount = 0;

        items.forEach(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const discountPercentage = parseFloat(item.discountPercentage) || 0;

            const itemSubtotal = quantity * unitPrice;
            const itemDiscount = itemSubtotal * (discountPercentage / 100);

            subtotal += itemSubtotal;
            totalDiscount += itemDiscount;
        });

        const taxAmount = (subtotal - totalDiscount) * (tax / 100);
        const total = (subtotal - totalDiscount) + taxAmount;

        return {
            subtotal,
            discountAmount: totalDiscount,
            taxAmount,
            total,
            taxRate: tax
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!selectedCompany) {
            alert(t("invoices.common.selectCompanyError"));
            return;
        }

        if (items.length === 0) {
            alert(t("invoices.common.addItemError"));
            return;
        }

        const { total, discountAmount, taxAmount } = calculateTotals();
        console.log( { total, discountAmount, taxAmount } )
        // Prepare invoice data matching the API expected format
        const invoiceData = {
            // userId: parseInt(userId),
            companyId: parseInt(formData.companyId),
            tax: taxAmount,
            items: items.map(item => ({
                id: item.id || 0,
                name: item.name,
                unitPrice: parseFloat(item.unitPrice) || 0,
                quantity: parseFloat(item.quantity) || 0,
                discountPercentage: parseFloat(item.discountPercentage) || 0,
            })),
            status: invoice?.status || "SUBMITTED",
        };

        updateMutation.mutate(invoiceData);
    };

    const { subtotal, discountAmount, taxAmount, total } = calculateTotals();

    if (invoiceLoading || companiesLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    return (
        <div className="p-6">
            <TaleDashbaordMainCard className="p-6">
                <div className="mb-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold">{t("invoices.admin.editTitle")}</h1>
                            <p className="text-gray-600">{t("invoices.common.invoiceNumber", { id: invoice?.invoiceNumber || `FACT-${invoice.id}/${year}` })}</p>
                        </div>
                        <TaleButton onClick={() => navigate(`/admin/factures/${id}`)}>
                            <i className="fas fa-arrow-left mr-2 rtl:mr-0 rtl:ml-2"></i>
                            {t("invoices.admin.backToInvoice")}
                        </TaleButton>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Company Selection */}
                        <CompanySelection
                            formData={formData}
                            handleInputChange={handleInputChange}
                            companies={companies}
                            companiesLoading={companiesLoading}
                            selectedCompany={selectedCompany}
                            setSelectedCompany={setSelectedCompany}
                        />

                        {/* Invoice Details */}
                        <div>
                            <h2 className="text-xl font-bold mb-4">{t("invoices.common.invoiceItems")}</h2>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <TaleInput
                                        label={t("invoices.common.issueDate")}
                                        name="creationDate"
                                        type="date"
                                        value={formData.creationDate}
                                        onChange={handleInputChange}
                                        required
                                    />

                                    <TaleInput
                                        label={t("invoices.common.dueDateOptional")}
                                        name="dueDate"
                                        type="date"
                                        value={formData.dueDate}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t("invoices.common.taxRate")}
                                    </label>
                                    <input
                                        type="number"
                                        value={tax}
                                        onChange={(e) => setTax(parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t("invoices.common.notesOptional")}
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder={t("invoices.common.notesPlaceholder")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Items */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{t("invoices.common.invoiceItems")}</h2>
                            <TaleButton type="button" onClick={addItem}>
                                <i className="fas fa-plus mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.common.addItem")}
                            </TaleButton>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                <tr className="bg-gray-50">
                                    <th className="border p-3 text-left rtl:text-right">{t("invoices.common.itemName")}</th>
                                    <th className="border p-3 text-left rtl:text-right">{t("invoices.common.quantity")}</th>
                                    <th className="border p-3 text-left rtl:text-right">{t("invoices.common.unitPrice")} <sup>(DT)</sup></th>
                                    <th className="border p-3 text-left rtl:text-right">{t("invoices.common.discountPercentHeader")}</th>
                                    <th className="border p-3 text-left rtl:text-right">{t("invoices.common.discountValueHeader")} <sup>(DT)</sup></th>
                                    <th className="border p-3 text-left rtl:text-right">{t("invoices.common.subtotalHeader")} <sup>(DT)</sup></th>
                                    <th className="border p-3 text-left rtl:text-right">{t("invoices.common.actions")}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => handleItemChange(index, "name", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                placeholder={t("invoices.common.itemName")}
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                min="1"
                                                step="1"
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="number"
                                                value={item.unitPrice}
                                                onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                min="0"
                                                step="0.01"
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="number"
                                                value={item.discountPercentage}
                                                onChange={(e) => handleItemChange(index, "discountPercentage", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                min="0"
                                                max="100"
                                                step="0.1"
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={new Intl.NumberFormat('fr-TN', {
                                                    style: 'currency',
                                                    currency: 'TND'
                                                }).format(item.discountValue || 0)}
                                                className="w-full px-2 py-1 border rounded bg-gray-50"
                                                readOnly
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={new Intl.NumberFormat('fr-TN', {
                                                    style: 'currency',
                                                    currency: 'TND'
                                                }).format(item.subtotal || 0)}
                                                className="w-full px-2 py-1 border rounded bg-gray-50"
                                                readOnly
                                            />
                                        </td>
                                        <td className="border p-2 text-center">
                                            <button
                                                type="button"
                                                onClick={() => removeItem(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {items.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                <i className="fas fa-file-invoice text-4xl mb-2"></i>
                                <p>{t("invoices.common.noItemsAdded")}</p>
                                <p className="text-sm mt-2">{t("invoices.common.addItemHint", { addItem: t("invoices.common.addItem") })}</p>
                            </div>
                        )}
                    </div>

                    {/* Summary and Actions */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                        {/* Summary */}
                        <div className="w-full lg:w-80">
                            <h3 className="text-lg font-bold mb-4">{t("invoices.common.invoiceSummary")}</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>{t("invoices.common.subtotalLabel")}</span>
                                    <span>
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'TND'
                                        }).format(subtotal)}
                                    </span>
                                </div>

                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                        <span>{t("invoices.common.totalDiscountLabel")}</span>
                                        <span>- {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'TND'
                                        }).format(discountAmount)}</span>
                                    </div>
                                )}

                                <div className="flex justify-between pt-2 border-t">
                                    <span>{t("invoices.common.amountAfterDiscount")}</span>
                                    <span className="font-semibold">
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'TND'
                                        }).format(Math.max(0, subtotal - discountAmount))}
                                    </span>
                                </div>

                                <div className="flex justify-between">
                                    <span>{t("invoices.common.vatPercent", { rate: tax })}</span>
                                    <span>
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'TND'
                                        }).format(taxAmount)}
                                    </span>
                                </div>

                                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                                    <span>{t("invoices.common.totalTTCLabel")}</span>
                                    <span>
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'TND'
                                        }).format(total)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-4">
                            <TaleButton
                                type="button"
                                variant="outlined"
                                onClick={() => navigate(`/admin/factures/${id}`)}
                                disabled={updateMutation.isPending}
                            >
                                {t("common.cancel")}
                            </TaleButton>
                            <TaleButton
                                type="submit"
                                loading={updateMutation.isPending}
                            >
                                <i className="fas fa-save mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.admin.saveChanges")}
                            </TaleButton>
                        </div>
                    </div>
                </form>
            </TaleDashbaordMainCard>
        </div>
    );
};

export default EditInvoice;
